import { useEffect, useState, useCallback, useRef } from 'react';
import websocketService from '../services/websocket';
import type { WebSocketMessage } from '../services/websocket';

interface UseWebSocketOptions {
  organizationId?: string;
  autoConnect?: boolean;
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: any) => void;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
  send: (data: any) => void;
  subscribe: (event: string, handler: (data: any) => void) => () => void;
}

export const useWebSocket = (options: UseWebSocketOptions = {}): UseWebSocketReturn => {
  const {
    organizationId,
    autoConnect = true,
    onMessage,
    onConnect,
    onDisconnect,
    onError,
  } = options;

  const [isConnected, setIsConnected] = useState(websocketService.isConnected());
  const handlersRef = useRef<Map<string, Set<Function>>>(new Map());
  const cleanupRef = useRef<(() => void)[]>([]);

  useEffect(() => {
    // Set up event listeners
    const handleConnected = () => {
      setIsConnected(true);
      onConnect?.();
    };

    const handleDisconnected = () => {
      setIsConnected(false);
      onDisconnect?.();
    };

    const handleError = (error: any) => {
      onError?.(error);
    };

    const handleMessage = (message: WebSocketMessage) => {
      onMessage?.(message);
    };

    websocketService.on('connected', handleConnected);
    websocketService.on('disconnected', handleDisconnected);
    websocketService.on('error', handleError);
    
    if (onMessage) {
      websocketService.on('message', handleMessage);
    }

    // Auto-connect if requested
    if (autoConnect && !websocketService.isConnected()) {
      websocketService.connect(organizationId);
    }

    // Clean up
    return () => {
      websocketService.off('connected', handleConnected);
      websocketService.off('disconnected', handleDisconnected);
      websocketService.off('error', handleError);
      
      if (onMessage) {
        websocketService.off('message', handleMessage);
      }

      // Clean up all subscriptions
      cleanupRef.current.forEach(cleanup => cleanup());
      cleanupRef.current = [];
    };
  }, [organizationId, autoConnect, onConnect, onDisconnect, onError, onMessage]);

  const connect = useCallback(() => {
    websocketService.connect(organizationId);
  }, [organizationId]);

  const disconnect = useCallback(() => {
    websocketService.disconnect();
  }, []);

  const send = useCallback((data: any) => {
    websocketService.send(data);
  }, []);

  const subscribe = useCallback((event: string, handler: (data: any) => void) => {
    // Add handler to WebSocket service
    websocketService.on(event, handler);

    // Store handler reference for cleanup
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)?.add(handler);

    // Return unsubscribe function
    const unsubscribe = () => {
      websocketService.off(event, handler);
      handlersRef.current.get(event)?.delete(handler);
    };

    cleanupRef.current.push(unsubscribe);
    return unsubscribe;
  }, []);

  return {
    isConnected,
    connect,
    disconnect,
    send,
    subscribe,
  };
};

export default useWebSocket;